import { Schema } from "effect"
import { AgentKind } from "./common"
import { AgentId, ItemId, Millis, ProfileId, RepoId, RunId, RunnerId, UserId } from "./ids"
import { SecretName } from "./secrets"

// #region placement
/**
 * Tool capability token: lowercase name, optional "@version". A requirement "xcode@26" is met by "xcode@26" or
 * "xcode@26.1"; a requirement "xcode" is met by any "xcode@*". Examples: simulator, docker, fastlane, computer-use.
 */
export const ToolCap = Schema.String.check(Schema.isPattern(/^[a-z][a-z0-9-]*(@[0-9][0-9a-z.]*)?$/))
export const Os = Schema.Literals(["macos", "linux", "windows"])
export const Trust = Schema.Literals(["local", "cloud", "hosted"])
/** Free-form "key=value" labels, e.g. net=home. */
export const Labels = Schema.Record(Schema.String, Schema.String)

/** Decision 0095: a place to avoid: a whole runner, or one profile on it. */
export const AvoidPlace = Schema.Struct({ runner: RunnerId, profile: Schema.optionalKey(ProfileId) })
export type AvoidPlace = typeof AvoidPlace.Type

/** Hard requirements. All must hold. Lists of things a run needs are "all of"; lists of alternatives are "any of". */
export const Requires = Schema.Struct({
  agent: Schema.optionalKey(Schema.NonEmptyArray(AgentKind)), // any of
  tools: Schema.optionalKey(Schema.Array(ToolCap)), // all of
  repos: Schema.optionalKey(Schema.Array(RepoId)), // all of
  os: Schema.optionalKey(Schema.NonEmptyArray(Os)), // any of
  trust: Schema.optionalKey(Schema.NonEmptyArray(Trust)), // any of
  labels: Schema.optionalKey(Labels), // all of
  /** Pins. `mind: runner:vps-1` compiles to { runner }. A pinned run waits for that runner; it never falls back. */
  runner: Schema.optionalKey(RunnerId),
  profile: Schema.optionalKey(ProfileId),
  /**
   * Decision 0095: places an agent chose to avoid (`retry_item{avoid}`). Hard: an avoided profile is
   * `profile_out_of_scope`. Only the hub reads it; runners ignore it.
   */
  avoid: Schema.optionalKey(Schema.Array(AvoidPlace)),
  /**
   * Decision 0141: secrets the run needs (all of), by name. The hub places the run only where they are delivered (the
   * Platform Runner, or a runner with the feature `secrets`) and only when each reaches the run's project and asker;
   * otherwise it waits with `missing_secret`. Only the hub reads it; runners ignore it.
   */
  secrets: Schema.optionalKey(Schema.Array(SecretName)),
})
export type Requires = typeof Requires.Type

/** Soft preference: the scheduler adds `weight` when `match` holds. */
export const Prefer = Schema.Struct({ match: Requires, weight: Schema.Number })

export const Affinity = Schema.Struct({
  /** Reuse the worktree/branch another item created (DIC-412 builds on DIC-388's branch). */
  worktreeOf: Schema.optionalKey(ItemId),
  /** Keep native-session locality: prefer the runner+profile that ran this agent's last session. */
  sessionOf: Schema.optionalKey(AgentId),
  /** soft adds score; hard turns it into a requirement and the run waits. */
  strength: Schema.Literals(["soft", "hard"]),
})

/**
 * Named mutexes taken atomically with the lease and released with it. "ws:" keys are workspace-wide
 * ("ws:repo:dictly:release"); "runner:" keys are per runner ("runner:computer-use": one GUI session per Mac).
 */
export const ExclusiveKey = Schema.String.check(Schema.isPattern(/^(ws|runner):[a-z0-9:_.-]+$/))

export const Placement = Schema.Struct({
  requires: Requires,
  prefers: Schema.Array(Prefer),
  affinity: Schema.NullOr(Affinity),
  exclusive: Schema.Array(ExclusiveKey),
})
export type Placement = typeof Placement.Type

/** Area defaults: placement without affinity (affinity is always about a specific item or agent). */
export const PlacementDefaults = Schema.Struct({
  requires: Requires,
  prefers: Schema.Array(Prefer),
  exclusive: Schema.Array(ExclusiveKey),
})

/** Why a run is waiting. Shown verbatim on the canvas ("waiting for runner: needs xcode@26; jannik-mbp is asleep"). */
export const Unmet = Schema.TaggedUnion({
  no_runner_online: {},
  runner_offline: { runnerId: RunnerId, lastSeenAt: Schema.NullOr(Millis) },
  runner_paused: { runnerId: RunnerId },
  /**
   * The runner may not take work for this principal (`mayRun`, decision 0063). Wave E adds the runner and its owner so
   * the line can say whose machine it was. Both absent on records from before E.
   */
  owner_lock: {
    principal: UserId,
    runnerId: Schema.optionalKey(RunnerId),
    ownerId: Schema.optionalKey(UserId),
    /**
     * Decision 0128: the subscription (profile) that may not run it: it is `ownerId`'s and personal. Absent = the whole
     * machine refused (wave E).
     */
    profileId: Schema.optionalKey(ProfileId),
    /**
     * 0128 addendum (2026-10-20): the workspace has a Platform Runner, so the asker may connect their own subscription
     * there. Absent = it has none: the line only says who could share.
     */
    platformRunner: Schema.optionalKey(Schema.Boolean),
  },
  ceiling_refuses: { runnerId: RunnerId, why: Schema.String },
  missing_tool: { tool: ToolCap },
  missing_repo: { repoId: RepoId },
  missing_agent: { agent: Schema.Array(AgentKind) },
  profile_not_ready: { runnerId: RunnerId, profileId: ProfileId, why: Schema.String },
  profile_out_of_scope: { runnerId: RunnerId, profileId: ProfileId },
  /**
   * Decision 0166 §3.6: the run's asker chose which subscriptions their work in this project uses, and this one is not
   * among them. `agent` is its CLI ("none of your chosen subscriptions here has Claude Code").
   */
  not_chosen: { runnerId: RunnerId, profileId: ProfileId, agent: AgentKind },
  profile_at_capacity: { runnerId: RunnerId, profileId: ProfileId },
  rate_limited: { runnerId: RunnerId, profileId: ProfileId, resetsAt: Schema.NullOr(Millis) },
  at_capacity: { runnerId: RunnerId },
  exclusive_held: { key: ExclusiveKey, heldBy: RunId },
  affinity_unmet: { worktreeOf: Schema.NullOr(ItemId) },
  /**
   * The owner's runner is too old for this run: the legacy JSON socket (below protocol 1, B11), or a manifest without a
   * feature the run needs (`item_model` for every run since decision 0039: "waiting for runner: minga ≥ 0.4.0").
   */
  runner_outdated: { runnerId: RunnerId, version: Schema.String, minimum: Schema.String },
  /**
   * Decision 0141: a secret the run needs does not reach it. `not_added`: none by that name reaches anything here;
   * `not_shared`: `ownerId`'s personal one exists but is not shared with the team; `not_in_project`: a team secret by
   * that name exists but does not list this project.
   */
  missing_secret: {
    name: SecretName,
    why: Schema.Literals(["not_added", "not_shared", "not_in_project"]),
    ownerId: Schema.optionalKey(UserId),
  },
})
export type Unmet = typeof Unmet.Type

/**
 * Wave E (decision 0063, §15 decision 2(a)): another person's personal runner that could take a waiting run if its
 * owner lent it to the item. The hub finds these when it records a wait (`schedule` with the owner lock lifted).
 * `needs` lists what only this runner meets (`missing_tool` and the rest), for "needs Xcode; only jannik-mbp has it".
 */
export const LendCandidate = Schema.Struct({
  runnerId: RunnerId,
  ownerId: UserId,
  needs: Schema.Array(Unmet),
  /**
   * Decision 0128: not a lend but a share: `ownerId`'s personal subscription (this profile on a team runner, e.g. the
   * Platform Runner) would run it if its owner made it Team. Absent = lend the machine to the item (wave E).
   */
  profileId: Schema.optionalKey(ProfileId),
  /** With `profileId`: that profile's CLI ("claude", "codex"), for "Share your Claude login…". */
  agent: Schema.optionalKey(AgentKind),
})
export type LendCandidate = typeof LendCandidate.Type

/**
 * Re-walk of 2026-10-28 (decision 0166 §2): a person's chat message to an agent that waits behind another turn, and
 * what its own turn would wait on (the hub's probe of that turn with them as its asker). The Project raises the share
 * cards from it at once, not only after the turn ahead ends; the asker's own turn request clears it.
 */
export const AskWait = Schema.Struct({
  agentId: AgentId,
  askerId: UserId,
  unmet: Schema.Array(Unmet),
  since: Millis,
  lendable: Schema.optionalKey(Schema.Array(LendCandidate)),
})
export type AskWait = typeof AskWait.Type

export const LeaseTiming = {
  LEASE_TTL_MS: 60_000,
  RENEW_EVERY_MS: 20_000,
  HEARTBEAT_EVERY_MS: 10_000,
  OFFER_TTL_MS: 10_000,
  MCP_TOKEN_GRACE_MS: 10_000,
  SELF_FENCE_MARGIN_MS: 5_000,
  INTERRUPT_FORCE_CLOSE_MS: 10_000,
  MAX_PRESTART_RELEASES: 3,
} as const
// #endregion placement
